import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useDocumentsLists } from "@/hooks/useDocumentsLists";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getDocIdsFromLists, isDocumentInSet } from "../../utils/documentUtils";
import { RequiredDocumentRow } from "./RequiredDocumentRow";

interface RequiredDocumentsSectionProps {
  talentProfileData: any;
  documents: any[];
  canUploadDocuments: boolean;
  onUpload: (id: number, name: string) => void;
  onView: (doc: any) => void;
  onEdit: (doc: any) => void;
  onDeleteUploaded: (id: number) => void;
  onRemoveRequirement: (id: number, type: "document" | "list") => void;
  canEditFile: (doc: any) => boolean;
}

export function RequiredDocumentsSection({
  talentProfileData,
  documents,
  canUploadDocuments,
  onUpload,
  onView,
  onEdit,
  onDeleteUploaded,
  onRemoveRequirement,
  canEditFile
}: RequiredDocumentsSectionProps) {
  const { profile: authProfile } = useAuth();
  const { t } = useTranslation();
  const { data: documentsLists = [] } = useDocumentsLists();
  const reqDocTypes =
    talentProfileData?.required_documents?.required_document_types || [];
  const reqDocLists =
    talentProfileData?.required_documents?.required_documents_lists || [];

  if (reqDocTypes.length === 0 && reqDocLists.length === 0) return null;

  const docsInLists = getDocIdsFromLists(reqDocLists, documentsLists);
  const standaloneDocTypes = reqDocTypes.filter(
    (id: number) => !isDocumentInSet(id, docsInLists)
  );

  const renderRow = (docId: number, canDelete: boolean) => (
    <RequiredDocumentRow
      key={`req-doc-${docId}`}
      docId={docId}
      canDeleteDocument={canDelete}
      documents={documents}
      canUploadDocuments={canUploadDocuments}
      talentProfileData={talentProfileData}
      onUpload={onUpload}
      onView={onView}
      onEdit={onEdit}
      onDeleteUploaded={onDeleteUploaded}
      onRemoveRequirement={onRemoveRequirement}
      canEditFile={canEditFile}
    />
  );

  return (
    <div className="mb-8 space-y-6">
      {reqDocLists.map((listId: number) => {
        const list = documentsLists.find((l) => l.id === listId);
        if (!list) return null;

        return (
          <div
            key={`req-list-${listId}`}
            className="p-4 bg-slate-50 border rounded-lg shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">{list.name}</h3>
              {authProfile?.isSuperUser && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveRequirement(listId, "list");
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t(
                    "common:documents.requirements.remove_list",
                    "Liste entfernen"
                  )}
                </Button>
              )}
            </div>
            {list.description && (
              <p className="text-sm text-gray-500 mb-3">{list.description}</p>
            )}
            <div className="space-y-2">
              {list.documents &&
                list.documents.map((docId: number) => renderRow(docId, false))}
            </div>
          </div>
        );
      })}

      {standaloneDocTypes.length > 0 && (
        <div className="p-4 bg-slate-50 border rounded-lg shadow-sm">
          <h3 className="text-lg font-medium mb-3">
            {reqDocLists.length > 0
              ? t(
                  "common:documents.requirements.more_required",
                  "Weitere erforderliche Dokumente"
                )
              : t(
                  "common:documents.requirements.required_docs",
                  "Erforderliche Dokumente"
                )}
          </h3>
          <div className="space-y-2">
            {standaloneDocTypes.map((docId: number) => renderRow(docId, true))}
          </div>
        </div>
      )}
    </div>
  );
}
